import { useState } from 'react';
import { Loader2, Sparkles, Workflow, AlertTriangle, ListOrdered, Bell, Zap } from 'lucide-react';
import Button from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import Badge from './ui/Badge';
import api from '../lib/api';

export interface WorkflowReport {
  summary: string;
  healthScore: number;
  stalledItems: Array<{
    type: string;
    title: string;
    daysStalled: number;
    owner: string;
    urgency: 'high' | 'medium' | 'low';
    suggestedAction: string;
  }>;
  slaAlerts: Array<{ metric: string; status: 'ok' | 'warning' | 'breach'; detail: string }>;
  taskPriorities: Array<{ priority: number; task: string; assignTo: string; reason: string }>;
  smartReminders: Array<{ audience: string; message: string; channel: string; timing: string }>;
  automations: Array<{ trigger: string; action: string; benefit: string }>;
  demo?: boolean;
  cached?: boolean;
}

interface AIWorkflowPanelProps {
  mode: 'job' | 'agency';
  jobId?: string;
  title?: string;
  className?: string;
}

function urgencyVariant(u: string): 'destructive' | 'warning' | 'secondary' {
  if (u === 'high') return 'destructive';
  if (u === 'medium') return 'warning';
  return 'secondary';
}

function slaVariant(s: string): 'success' | 'warning' | 'destructive' {
  if (s === 'breach') return 'destructive';
  if (s === 'warning') return 'warning';
  return 'success';
}

export function AIWorkflowPanel({
  mode,
  jobId,
  title = mode === 'job' ? 'Workflow Automation' : 'Agency Workflow Orchestration',
  className = '',
}: AIWorkflowPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<WorkflowReport | null>(null);

  const runScan = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = mode === 'job' ? '/ai/workflow/job-scan' : '/ai/workflow/agency-scan';
      const payload = mode === 'job' ? { jobId } : {};
      const res = await api.post(endpoint, payload);
      setReport(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to scan workflow';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={`border-emerald-200/60 bg-emerald-50/30 dark:bg-emerald-950/20 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Workflow className="h-5 w-5 text-emerald-600" />
          {title}
        </CardTitle>
        <CardDescription>
          Detect stalled items, SLA breaches, and get prioritized tasks with smart reminder suggestions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runScan} disabled={loading} size="sm">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Run Workflow Scan
              </>
            )}
          </Button>
          {report?.demo && <Badge variant="outline">Demo mode</Badge>}
          {report?.cached && <Badge variant="outline">Cached</Badge>}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {report && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border bg-white/60 dark:bg-black/20">
              <div className="text-3xl font-bold text-emerald-600">{report.healthScore}</div>
              <div>
                <p className="font-medium">Workflow health score</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{report.summary}</p>
              </div>
            </div>

            {report.slaAlerts.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <AlertTriangle className="h-4 w-4" /> SLA status
                </p>
                <div className="space-y-2">
                  {report.slaAlerts.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <Badge variant={slaVariant(a.status)}>{a.status}</Badge>
                      <span className="font-medium">{a.metric}</span>
                      <span className="text-gray-600">— {a.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.stalledItems.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Stalled items</p>
                <div className="space-y-2">
                  {report.stalledItems.map((item, i) => (
                    <div key={i} className="rounded-lg border p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge variant="outline">{item.type}</Badge>
                        <span className="font-medium">{item.title}</span>
                        <Badge variant={urgencyVariant(item.urgency)}>{item.urgency}</Badge>
                        <span className="text-xs text-gray-500">{item.daysStalled}d stalled</span>
                      </div>
                      <p className="text-gray-600">Owner: {item.owner}</p>
                      <p className="text-emerald-700 dark:text-emerald-300">→ {item.suggestedAction}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.taskPriorities.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <ListOrdered className="h-4 w-4" /> Priority tasks
                </p>
                <ol className="space-y-2">
                  {report.taskPriorities
                    .sort((a, b) => a.priority - b.priority)
                    .map((t, i) => (
                      <li key={i} className="rounded-lg border p-3 text-sm">
                        <p className="font-medium">
                          #{t.priority} {t.task}
                        </p>
                        <p className="text-gray-600">
                          Assign to: {t.assignTo} — {t.reason}
                        </p>
                      </li>
                    ))}
                </ol>
              </div>
            )}

            {report.smartReminders.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <Bell className="h-4 w-4" /> Smart reminders
                </p>
                <div className="space-y-2">
                  {report.smartReminders.map((r, i) => (
                    <div key={i} className="rounded-lg border p-3 text-sm bg-blue-50/50 dark:bg-blue-950/20">
                      <p className="font-medium">{r.audience}</p>
                      <p className="text-gray-700 dark:text-gray-300">{r.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {r.channel} · {r.timing}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.automations.length > 0 && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                  <Zap className="h-4 w-4" /> Suggested automations
                </p>
                <ul className="space-y-2">
                  {report.automations.map((a, i) => (
                    <li key={i} className="rounded-lg border p-3 text-sm">
                      <p>
                        <span className="font-medium">When:</span> {a.trigger}
                      </p>
                      <p>
                        <span className="font-medium">Then:</span> {a.action}
                      </p>
                      <p className="text-emerald-700 dark:text-emerald-300 text-xs mt-1">{a.benefit}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
